/**
 * ImportCalibreCheck — calibre check step in the bulk import flow.
 * - Scans entries for unique calibre/movement values
 * - Supports extraction rules to find calibres from other text fields
 * - Matches each against the custom calibre DB, honouring per-alias brand scope
 * - Provides modals for adding to DB and mapping slugs
 * - Rescan support after adding or editing calibres elsewhere
 * - Enriches entries with calibre data from DB
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { CalibreSlugMappingsModal } from './CalibreSlugMappingsModal';
import { CustomCalibreModal } from './CustomCalibreModal';
import { CalibreActionToolbar, isGenericMovementName } from './CalibreActionToolbar';
import {
  getCalibreFamilyById,
  getCustomCalibres,
  saveCustomCalibre,
  type CalibreData,
  type CustomCalibreEntry,
} from '../../services/firebase';
import { buildCalibreMatchIndex, normalizeCalibreKey } from '../../services/calibreResolution';
import {
  detectTierSuffixCandidates,
  type TierSuffixCandidate,
} from './calibreTierSuffix';
import { detectTierFromCalibreString, tiersForBrand } from './calibreTiers';
import { bulkRevisionNotice, currentMember, firstYear, namesExplicitRevision } from '../../services/calibreLineageLogic';
import {
  acceptedCalibreBrandId,
  soleManufacturerIdForBrand,
} from '../../services/calibreClassification';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';
import { useDocsLinks } from '../../hooks/useDocsLinks';
import { DocsLinkChip } from '../common/DocsLinkChip';
import {
  buildExtractorCalibreIndex,
  calibreSeedToData,
  extractedCalibreToSeed,
  extractorCalibreKey,
  type CalibreCreateSeed,
} from './adapters/extractionToCalibres';
import type { ExtractedCalibre } from '@watchkeeper/shared-types';
import type { RevisionLineMember } from './importCalibreStamp';
import toast from 'react-hot-toast';

interface ScrapedEntry {
  movement: string;
  [key: string]: unknown;
}

interface CalibreStatus {
  calibreName: string;
  /**
   * `custom_calibres/{id}` for a row that resolved to a real calibre — the
   * authoritative link the import writes as `WatchReference.calibreId`.
   *
   * Undefined for a row that never resolved, and for one whose calibre was just
   * created outside this component without reporting an id back. Both mean the
   * same thing to the importer: write the legacy string, link nothing.
   */
  id?: string;
  normalizedName?: string;
  /**
   * `data.tiers[].id` on the matched calibre — the tier every reference carrying
   * this string will link to.
   *
   * Two things set it: the base+tier SPLIT of a suffixed string ("Ball
   * RR1102-CSL" → RR1102, tier CSL), and the curator's own pick in the row's
   * tier selector. Unset means "nobody has said", which leaves the decision to
   * `stampCalibreOnEntry`'s text detector — NOT the same thing as "no tier",
   * which is {@link tierCleared}.
   */
  tierId?: string;
  /** The tier's canonical name, for the row's source line. */
  tierName?: string;
  /**
   * The curator explicitly chose "— No tier —" for this row. Distinct from an
   * absent `tierId`: it has to survive into `stampCalibreOnEntry` and suppress
   * the text detector there, or clearing a tier off a row whose scraped string
   * still reads "…COSC" would silently put it straight back.
   */
  tierCleared?: boolean;
  /**
   * Where the current `tierId` came from — `'auto'` for a detected split,
   * `'manual'` once the curator has touched the selector. Display only: it
   * drives the "auto" hint, so an admin can tell a machine reading from their
   * own without re-checking the source string.
   */
  tierSource?: 'auto' | 'manual';
  /**
   * Competing base+tier readings of the same string, when more than one survived
   * detection. Present ⇒ nothing was auto-applied and the user picks.
   */
  tierCandidates?: TierSuffixCandidate[];
  count: number;
  status: 'pending' | 'checking' | 'found' | 'not_found' | 'error';
  source?: string;
  data?: CalibreData;
  error?: string;
}

export interface ExtractionRule {
  id: string;
  sourceField: string;
  pattern: string; // regex or contains text
  // Where the extracted value goes (default: calibre). 'module' routes the
  // extracted value to the Module extraction step (digital watches) instead of
  // the calibre step — the electronic counterpart of calibre/baseCalibre.
  targetField?: 'calibre' | 'movement' | 'module';
  replaceWith?: string; // optional: use this value instead of the extracted match
}

const EXTRACTION_SOURCES = [
  { value: 'description', label: 'Description' },
  { value: 'title', label: 'Title' },
  { value: 'variant', label: 'Variant' },
  { value: 'tags', label: 'Tags' },
  { value: 'productName', label: 'Product Name' },
];

let ruleId = 0;

export interface CalibreMapEntry {
  data: CalibreData;
  /**
   * `custom_calibres/{id}` — carried through so the importer can write the
   * authoritative `calibreId` link and not just the free-text string. The match
   * that produced this entry already knows the id; every earlier version of
   * this step dropped it here and left the importer with a name alone.
   */
  id?: string;
  normalizedName?: string;
  /**
   * `data.tiers[].id` on that calibre, when the check step resolved a tier for
   * the row — by splitting a suffixed string, or because the curator picked one.
   * `stampCalibreOnEntry` prefers it over its own text-based tier detection: an
   * explicit answer beats an inference drawn from the same string.
   */
  tierId?: string;
  /** The tier's canonical name, carried for the importer's preview chip. */
  tierName?: string;
  /**
   * The curator explicitly chose "— No tier —". `stampCalibreOnEntry` must skip
   * its text detection entirely rather than treat the absent `tierId` as an
   * invitation to guess. See {@link CalibreStatus.tierCleared}.
   */
  tierCleared?: boolean;
  /**
   * Every live member of the matched calibre's revision line (itself included),
   * when it is in one. The importer resolves each reference to a revision from
   * these — the match names a line, the ref's year names the revision.
   */
  revisionLine?: RevisionLineMember[];
}

interface ImportCalibreCheckProps {
  entries: ScrapedEntry[];
  brandName: string;
  /** Optional brand ID — used to scope AKA aliases to a specific brand when adding aliases. */
  brandId?: string;
  extractionRules: ExtractionRule[];
  /**
   * Calibre pages scraped alongside the watches, when the upload was an
   * extractor `ExtractionResult`. Empty for every other import source. Rows
   * whose name matches one of these get a "From Extractor" action that opens
   * the calibre editor pre-filled from the scrape.
   */
  extractorCalibres?: ExtractedCalibre[];
  /**
   * The brand's admin-curated manufacturer link overrides, straight off the
   * brand document. Feed the manufacturer defaulting below, which is otherwise
   * decided entirely from the manufacturer side of the relationship.
   */
  manufacturerIdsManualInclude?: string[];
  manufacturerIdsManualExclude?: string[];
  onExtractionRulesChange: (rules: ExtractionRule[]) => void;
  onProceed: (calibreMap: Map<string, CalibreMapEntry>) => void;
  onBack: () => void;
}

export function ImportCalibreCheck({
  entries,
  brandName,
  brandId,
  extractionRules,
  extractorCalibres,
  manufacturerIdsManualInclude,
  manufacturerIdsManualExclude,
  onExtractionRulesChange,
  onProceed,
  onBack,
}: ImportCalibreCheckProps) {
  const [calibres, setCalibres] = useState<CalibreStatus[]>([]);
  const [showJson, setShowJson] = useState(false);
  const [jsonLimit, setJsonLimit] = useState(10);
  const [testResults, setTestResults] = useState<Map<string, number> | null>(null);
  const [showSlugMappings, setShowSlugMappings] = useState(false);
  const [showCustomCalibres, setShowCustomCalibres] = useState(false);
  const [localDb, setLocalDb] = useState<CustomCalibreEntry[]>([]);
  const [localDbLoaded, setLocalDbLoaded] = useState(false);
  // Years of the entries behind each calibre string — the evidence that lets a
  // non-current revision pass without a notice (memo D14).
  const yearsByCalibre = useMemo(() => {
    const out = new Map<string, Array<number | null>>();
    for (const e of entries) {
      const key = String(e.movement ?? '').trim();
      if (!key) continue;
      const r = e as Record<string, unknown>;
      const year = firstYear(r.releaseYear) ?? firstYear(r.productionYears) ?? firstYear(r.startYear) ?? firstYear(r.year);
      out.set(key, [...(out.get(key) ?? []), year]);
    }
    return out;
  }, [entries]);
  // The row notice for a matched lineage member that is not current. Read off
  // the already-loaded catalogue, so it costs no reads.
  const revisionNoticeFor = (cal: CalibreStatus): string | null => {
    if (cal.status !== 'found' || !cal.id) return null;
    const matched = localDb.find((c) => c.id === cal.id);
    if (!matched?.calibreLineageId) return null;
    const lineMembers = localDb.filter((c) => c.calibreLineageId === matched.calibreLineageId && c.deprecated !== true);
    const current = currentMember(lineMembers);
    // A string that does not name the revision ("ETA 2824") is resolved per
    // reference on the review grid, so a "non-current" notice would mislead.
    if (lineMembers.length > 1 && !namesExplicitRevision(cal.calibreName, matched)) {
      return 'Revision line — each reference gets a revision by its year, else the brand’s founded year (≥ 2000), else the current revision. Review per row on the next screens.';
    }
    return bulkRevisionNotice(matched, current, yearsByCalibre.get(cal.calibreName) ?? []);
  };
  const [bulkCreating, setBulkCreating] = useState(false);
  // "From Extractor" flow: the row we opened the pre-filled create form for,
  // and the seed values themselves. Both null when the form is closed — the
  // modal's open state is derived from `extractorSeed` so each open re-reads a
  // fresh seed (the modal captures initialCreateValues at open time only).
  const [extractorSeed, setExtractorSeed] = useState<CalibreCreateSeed | null>(null);
  const [extractorSeedRow, setExtractorSeedRow] = useState<string | null>(null);
  // Snapshotted with the seed, NOT read live. The manufacturer catalogue loads
  // asynchronously, so a live value can flip undefined → id while the form is
  // open — and the modal re-seeds (discarding the admin's edits) whenever a
  // default prop changes. Freezing it at open time keeps that effect quiet.
  const [extractorSeedManufacturerId, setExtractorSeedManufacturerId] =
    useState<string | undefined>(undefined);

  // Load the custom calibre DB once on mount so we can resolve matches without network calls
  useEffect(() => {
    let cancelled = false;
    getCustomCalibres()
      .then((data) => {
        if (cancelled) return;
        setLocalDb(data);
        setLocalDbLoaded(true);
      })
      .catch((err) => {
        console.error('Failed to load custom calibres for local matching:', err);
        if (!cancelled) setLocalDbLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  /**
   * Re-read the calibre DB and rebuild the match index.
   *
   * `fromServer` forces a server read. The default `getDocs` may replay a
   * cached snapshot, which is exactly the "I fixed the calibre and it still
   * doesn't match" case — so every explicit refresh (the Rescan button, and
   * every create/edit made inside the wizard) asks the server, while the
   * once-per-mount initial load stays cheap.
   */
  const [rescanning, setRescanning] = useState(false);
  const refreshLocalDb = useCallback(async (fromServer = true) => {
    try {
      setLocalDb(await getCustomCalibres(fromServer));
    } catch (err) {
      console.error('Failed to refresh custom calibres:', err);
    }
  }, []);

  const handleRescan = useCallback(async () => {
    setRescanning(true);
    await refreshLocalDb(true);
    setRescanning(false);
  }, [refreshLocalDb]);

  // Build a fast lookup index from the local DB: normalized name & alsoKnownAs → entry
  const normalizeKey = normalizeCalibreKey;

  const localIndex = useMemo(
    // Brand scope over the calibre's FULL brand set (primary `brandId` plus every
    // brand-scoped alias), then the per-name rule. Filtering on the primary alone
    // discarded "NOMOS Alpha" (primary: d-malchert) before its Nomos-scoped
    // `Alpha` alias was ever read, so the row sat unmatched however it was scoped.
    () => buildCalibreMatchIndex(localDb, brandId, brandName),
    [localDb, brandId, brandName],
  );

  /**
   * `calibre_families/{id}.tierNames` for the families the loaded calibres
   * actually belong to — the suffix vocabulary the base+tier split runs on.
   *
   * Driven from the CALIBRES rather than by reading the families collection: the
   * calibres are already in memory with their `familyId`, the set of distinct
   * families among them is small, and a family with no member in scope could
   * never produce a match anyway. So this is a handful of `getDoc`s, once per
   * loaded DB, and none of them wasted.
   */
  const [familyTierNames, setFamilyTierNames] = useState<Map<string, string[]>>(
    new Map(),
  );
  /**
   * False until the family vocabulary above has been fetched at least once.
   *
   * The match effect lists `familyTierNames` as a dependency, so it runs TWICE
   * on mount: once the moment the calibre DB lands (with an empty vocabulary
   * map) and again when the families arrive. Rows that resolve by base+tier
   * split only appear on the second pass — so proceeding in between shipped an
   * import with those tiers silently missing, and looked exactly like the tier
   * detection having failed. Nothing surfaced the window; this flag is what the
   * action buttons wait on.
   */
  const [familyVocabLoaded, setFamilyVocabLoaded] = useState(false);

  useEffect(() => {
    // Every re-run reopens the window, so every re-run reopens the gate. A
    // Rescan replaces `localDb` and re-fetches the vocabulary, and `rescanning`
    // is already false by then — without this the second load would race the
    // buttons exactly as the first one did.
    setFamilyVocabLoaded(false);
    const familyIds = [
      ...new Set(
        localDb
          .map((c) => c.familyId?.trim())
          .filter((id): id is string => !!id),
      ),
    ];
    if (familyIds.length === 0) {
      setFamilyTierNames(new Map());
      setFamilyVocabLoaded(true);
      return;
    }
    let cancelled = false;
    Promise.all(
      familyIds.map(async (id) => {
        try {
          const fam = await getCalibreFamilyById(id);
          return [id, fam?.tierNames ?? []] as const;
        } catch (err) {
          // A family that won't load simply proposes no suffixes — the row falls
          // through to today's behaviour rather than the step failing.
          console.warn('[ImportCalibreCheck] family tier vocab load failed', id, err);
          return [id, [] as string[]] as const;
        }
      }),
    ).then((pairs) => {
      if (cancelled) return;
      setFamilyTierNames(
        new Map(pairs.filter(([, names]) => names.length > 0)),
      );
      setFamilyVocabLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [localDb]);

  /**
   * True while the resolver still has work to do — either the calibre DB or the
   * family tier vocabulary is outstanding.
   *
   * Every action that consumes resolver OUTPUT, or races the load, waits on
   * this. Proceed is the one that mattered: `handleProceed` builds its map from
   * `calibres`, which is `[]` until the first pass completes, so an early click
   * shipped an empty map — every reference imported with a free-text calibre
   * string, no `calibreId` and no tier, with no error to show for it.
   *
   * Deliberately NOT applied to Back, the extraction-rule editor, Test Rules or
   * View JSON. None of them read resolver output (the rule editor mutates props
   * and the row list re-derives itself), so none can produce a wrong import —
   * and disabling an escape hatch while the user waits is worse than the risk it
   * removes.
   */
  const resolving = !localDbLoaded || !familyVocabLoaded;

  // Scraped calibre pages, keyed for name matching against the pending rows.
  // Empty map for every non-extractor import, which switches the feature off.
  const extractorIndex = useMemo(
    () => buildExtractorCalibreIndex(extractorCalibres ?? []),
    [extractorCalibres],
  );

  // Manufacturer catalogue, for defaulting `manufacturerId` on everything this
  // step creates. Loaded only in a brand context because that's the only case
  // the answer can be derived from — one Algolia request, and the shared hook
  // keeps it current if a manufacturer is created elsewhere mid-import.
  const { manufacturers } = useManufacturerCatalogue({ enabled: !!brandId });

  /**
   * The brand's single linked manufacturer, or `undefined` when that's
   * ambiguous. An import is always scoped to one brand, so this is knowable
   * without asking — but only when the brand has exactly ONE non-generic
   * manufacturer. Zero (an unlinked brand, or one powered solely by a generic
   * maker) and two-or-more (a brand with an in-house arm AND a co-developer)
   * both leave the field blank for the admin, rather than writing a guess into
   * the FK watchlock derives `manufacturerIdAncestors` from.
   */
  const brandManufacturerId = useMemo(
    () =>
      soleManufacturerIdForBrand(brandId, manufacturers, {
        include: manufacturerIdsManualInclude,
        exclude: manufacturerIdsManualExclude,
      }),
    [brandId, manufacturers, manufacturerIdsManualInclude, manufacturerIdsManualExclude],
  );

  /**
   * The same value, vetoed for a recognisably third-party movement name. A
   * brand-scoped import still throws up "ETA 2824-2" rows, and there the brand
   * (and its in-house manufacturer) is the movement's USER, not its maker —
   * exactly the reasoning behind the existing `brandId`/`brandName` veto on the
   * same rows.
   */
  const manufacturerIdForRow = useCallback(
    (calibreName: string) =>
      isGenericMovementName(calibreName) ? undefined : brandManufacturerId,
    [brandManufacturerId],
  );

  // Extract unique calibres from entries (including extraction rules)
  const extractCalibres = useCallback(() => {
    const map = new Map<string, number>();

    entries.forEach((e) => {
      // From movement field
      const mov = (e.movement as string || '').trim();
      if (mov && mov.length > 1) {
        map.set(mov, (map.get(mov) || 0) + 1);
      }

      // From extraction rules (skip module-targeted rules — those feed the
      // separate Module extraction step, not the calibre list)
      for (const rule of extractionRules) {
        if (rule.targetField === 'module') continue;
        const fieldVal = String(e[rule.sourceField] || '');
        if (!fieldVal) continue;
        try {
          const regex = new RegExp(rule.pattern, 'i');
          const match = fieldVal.match(regex);
          if (match) {
            // Use replaceWith value if specified, otherwise use the extracted match
            const extracted = rule.replaceWith?.trim() || (match[1] || match[0]).trim();
            if (extracted && extracted.length > 1) {
              map.set(extracted, (map.get(extracted) || 0) + 1);
            }
          }
        } catch {
          // invalid regex — skip
        }
      }
    });

    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({
        calibreName: name,
        count,
        status: 'pending' as const,
      }));
  }, [entries, extractionRules]);

  // Apply local matches once both the DB and the extracted calibre list are ready
  useEffect(() => {
    if (!localDbLoaded) {
      // Don't show pending entries flickering — wait for local DB
      return;
    }
    const extracted = extractCalibres();
    const populated: CalibreStatus[] = extracted.map((cal) => {
      const hit = localIndex.get(normalizeKey(cal.calibreName));
      if (hit) {
        // The string matched a calibre whole — but it can still NAME a tier the
        // way "Sellita SW200-1 COSC" does once "…COSC" is an alias of the base.
        // Run the same text detector the importer would, so the selector below
        // shows what this row is actually going to import. Without it the row
        // read "— No tier —" here and arrived at the reference list carrying a
        // tier anyway, because `stampCalibreOnEntry` re-derived one from the
        // same string a step later — and the two screens disagreed.
        //
        // Seeding it here also collapses the two paths: with `tierId` set, the
        // stamp uses it as-is instead of detecting again, so what the curator
        // sees and can override is what gets written.
        const hitTiers = tiersForBrand(hit.data?.tiers, brandId);
        const detected = detectTierFromCalibreString(cal.calibreName, hitTiers);
        return {
          ...cal,
          status: 'found',
          source: 'local',
          id: hit.id,
          data: hit.data,
          normalizedName: hit.name,
          tierId: detected?.tierId,
          tierName: detected
            ? hitTiers.find((t) => t.id === detected.tierId)?.name
            : undefined,
          tierSource: detected ? ('auto' as const) : undefined,
        };
      }

      // Nothing matched the string whole. Before giving up on the row, try
      // reading it as BASE + TIER SUFFIX against the family vocabulary — the
      // difference between one action and six hundred when a brand names every
      // reference "<calibre>-<grade>".
      const candidates = detectTierSuffixCandidates({
        calibreString: cal.calibreName,
        familyTierNames,
        resolveBase: (name) => localIndex.get(normalizeKey(name)),
        brandId,
      });
      if (candidates.length === 1) {
        const [c] = candidates;
        return {
          ...cal,
          status: 'found',
          source: 'local-tier-suffix',
          id: c.calibreId,
          data: localIndex.get(normalizeKey(c.baseText))?.data ?? {},
          normalizedName: c.calibreName,
          tierId: c.tierId,
          tierName: c.tierName,
          tierSource: 'auto',
        };
      }
      // Two bases both read the string. Auto-applying either would be a guess,
      // so the row stays pending and offers the readings.
      if (candidates.length > 1) return { ...cal, tierCandidates: candidates };
      return cal;
    });
    setCalibres(populated);
  }, [extractCalibres, localIndex, localDbLoaded, familyTierNames, brandId]);

  // Toolbar success: mark the row as found locally and refresh DB so future
  // imports see the new entry. The toolbar already shows its own toast.
  const handleToolbarSuccess = useCallback(
    (
      calibreName: string,
      calibreId?: string,
      normalizedName?: string,
      data: CalibreData = {},
    ) => {
      setCalibres((prev) =>
        prev.map((c) =>
          c.calibreName === calibreName
            ? {
                ...c,
                status: 'found',
                source: 'local',
                // The toolbar reports the calibre it created or aliased, so the
                // row can link immediately rather than waiting for the DB
                // refresh below to re-match it through the index.
                id: calibreId || c.id,
                data,
                normalizedName: normalizedName || calibreName.trim(),
                error: undefined,
              }
            : c,
        ),
      );
      void refreshLocalDb();
    },
    [],
  );

  // Open the calibre editor's create form pre-filled from the scraped calibre
  // page. Opt-in per row on purpose — a scrape is a suggestion, not a source of
  // truth, so nothing is written until the admin reviews and saves.
  const handleOpenExtractorSeed = useCallback(
    (calibreName: string) => {
      const match = extractorIndex.get(extractorCalibreKey(calibreName));
      if (!match) return;
      setExtractorSeed(extractedCalibreToSeed(calibreName, match));
      setExtractorSeedRow(calibreName);
      setExtractorSeedManufacturerId(manufacturerIdForRow(calibreName));
    },
    [extractorIndex, manufacturerIdForRow],
  );

  const closeExtractorSeed = useCallback(() => {
    setExtractorSeed(null);
    setExtractorSeedRow(null);
    setExtractorSeedManufacturerId(undefined);
  }, []);

  const unmatchedCalibres = useMemo(
    () => calibres.filter((c) => c.status === 'not_found' || c.status === 'error' || c.status === 'pending'),
    [calibres],
  );

  const handleCreateAllUnmatched = useCallback(async () => {
    if (unmatchedCalibres.length === 0) return;
    if (!window.confirm(
      `Create ${unmatchedCalibres.length} new calibre entries for "${brandName}"? Each will be created as an empty entry (no AI enrichment).`,
    )) return;
    setBulkCreating(true);
    let success = 0;
    let failed = 0;
    for (const cal of unmatchedCalibres) {
      try {
        const isGeneric = isGenericMovementName(cal.calibreName);
        const docId = await saveCustomCalibre(
          cal.calibreName.trim(),
          {},
          // brandName stays for the legacy free-text column, but `brandId` below
          // is what a new write is for: an import always knows the brand it is
          // running for, so writing only the display name and asking every later
          // consumer to resolve it back to a document is a round trip that loses
          // (and mis-resolves) the answer we already hold. It also gives
          // saveCustomCalibre the canonical slug for the doc id. Matches the
          // per-row "Create New" path in CalibreActionToolbar.
          isGeneric ? undefined : (brandName || undefined),
          undefined,
          undefined,
          undefined,
          false,
          undefined,
          undefined,
          true,
          // Not "unknown for a stub" any more: the import knows its brand, and
          // when that brand resolves to exactly one manufacturer the FK is a
          // fact, not a guess. Blank whenever it isn't (see brandManufacturerId)
          // and for a generic movement name, so an ETA row never gets stamped
          // with the brand's in-house maker.
          isGeneric ? undefined : brandManufacturerId,
          undefined, // aliasDetails
          acceptedCalibreBrandId({ contextBrandId: brandId, isGeneric }),
        );
        setCalibres((prev) =>
          prev.map((c) =>
            c.calibreName === cal.calibreName
              ? { ...c, status: 'found', source: 'local', id: docId, data: {}, normalizedName: cal.calibreName.trim(), error: undefined }
              : c,
          ),
        );
        success++;
      } catch (err) {
        console.error('Bulk create failed for', cal.calibreName, err);
        failed++;
      }
    }
    void refreshLocalDb();
    setBulkCreating(false);
    if (failed === 0) {
      toast.success(`Created ${success} calibre${success === 1 ? '' : 's'}`);
    } else {
      toast.error(`Created ${success}, ${failed} failed`);
    }
  }, [unmatchedCalibres, brandName, brandId, brandManufacturerId]);

  /**
   * The unresolved rows the extractor also scraped a calibre page for — the
   * subset "Create All From Extractor" acts on. Rows with no scraped page are
   * NOT in here and are NOT created blank: this button's contract is "create
   * these from real specs", and quietly folding in empty stubs would make the
   * count meaningless. "Create All Unmatched" is the button for those, and
   * running it afterwards picks up whatever this one left behind.
   */
  const extractorUnmatched = useMemo(
    () =>
      unmatchedCalibres.filter((c) =>
        extractorIndex.has(extractorCalibreKey(c.calibreName)),
      ),
    [unmatchedCalibres, extractorIndex],
  );

  /**
   * Bulk counterpart of the per-row "From Extractor" pill: create every
   * extractor-matched row at once, skipping the review form.
   *
   * That form is deliberately the quality gate on the per-row path — a scrape is
   * a suggestion, and its units are best-effort. This button trades that gate
   * for throughput when a run comes back with a scraped page for most rows, so
   * the confirm says exactly what is being given up. Everything written here is
   * still editable in the calibre editor afterwards.
   */
  const handleCreateAllFromExtractor = useCallback(async () => {
    if (extractorUnmatched.length === 0) return;
    if (!window.confirm(
      `Create ${extractorUnmatched.length} calibre entries for "${brandName}" from the extractor's scraped specs?\n\n` +
      'These are written WITHOUT the per-row review form — the specs are unverified scrape data and units are ' +
      'best-effort. Rows with no scraped calibre page are skipped; use "Create All Unmatched" for those.',
    )) return;
    setBulkCreating(true);
    let success = 0;
    let failed = 0;
    for (const cal of extractorUnmatched) {
      const match = extractorIndex.get(extractorCalibreKey(cal.calibreName));
      if (!match) continue;
      // Through the seed, so this writes exactly what the pill's form would have
      // shown — the row's own name, never the scrape's.
      const seed = extractedCalibreToSeed(cal.calibreName, match);
      const data = calibreSeedToData(seed);
      try {
        const isGeneric = isGenericMovementName(cal.calibreName);
        const docId = await saveCustomCalibre(
          cal.calibreName.trim(),
          data,
          isGeneric ? undefined : (brandName || undefined),
          undefined,
          undefined,
          seed.sourceUrl,
          false,
          seed.description,
          undefined,
          true,
          isGeneric ? undefined : brandManufacturerId,
          undefined, // aliasDetails
          acceptedCalibreBrandId({ contextBrandId: brandId, isGeneric }),
        );
        setCalibres((prev) =>
          prev.map((c) =>
            c.calibreName === cal.calibreName
              ? { ...c, status: 'found', source: 'local', id: docId, data, normalizedName: cal.calibreName.trim(), error: undefined }
              : c,
          ),
        );
        success++;
      } catch (err) {
        console.error('Bulk extractor create failed for', cal.calibreName, err);
        failed++;
      }
    }
    void refreshLocalDb();
    setBulkCreating(false);
    if (failed === 0) {
      toast.success(`Created ${success} calibre${success === 1 ? '' : 's'} from extractor data`);
    } else {
      toast.error(`Created ${success}, ${failed} failed`);
    }
  }, [extractorUnmatched, extractorIndex, brandName, brandId, brandManufacturerId]);

  /**
   * Accept one base+tier reading of an ambiguous row, or undo an auto-applied
   * one. Detection is a heuristic over strings the curator did not write for us,
   * so both directions have to be one click — a wrong auto-map that can't be
   * undone is worse than no auto-map at all.
   */
  const handlePickTierCandidate = useCallback(
    (calibreName: string, candidate: TierSuffixCandidate) => {
      setCalibres((prev) =>
        prev.map((c) =>
          c.calibreName === calibreName
            ? {
                ...c,
                status: 'found',
                source: 'local-tier-suffix',
                id: candidate.calibreId,
                data: localIndex.get(normalizeKey(candidate.baseText))?.data ?? {},
                normalizedName: candidate.calibreName,
                tierId: candidate.tierId,
                tierName: candidate.tierName,
                tierSource: 'auto',
                tierCleared: undefined,
                tierCandidates: undefined,
                error: undefined,
              }
            : c,
        ),
      );
    },
    [localIndex, normalizeKey],
  );

  /**
   * Set (or clear) the tier for one row from its selector.
   *
   * Rows here are unique calibre STRINGS, not references — so one pick lands on
   * every reference carrying that string. That is the point: the alternative was
   * six hundred per-row decisions.
   *
   * `tierId === ''` is the explicit "— No tier —", recorded as `tierCleared` so
   * the importer's text detector can tell it apart from a row nobody has
   * touched. Both are `tierId: undefined`; only one of them means "no".
   */
  const handlePickTier = useCallback((calibreName: string, tierId: string) => {
    setCalibres((prev) =>
      prev.map((c) => {
        if (c.calibreName !== calibreName) return c;
        const tier = tiersForBrand(c.data?.tiers, brandId).find((t) => t.id === tierId);
        return {
          ...c,
          tierId: tier?.id,
          tierName: tier?.name,
          tierCleared: !tier,
          tierSource: 'manual' as const,
        };
      }),
    );
  }, [brandId]);

  const handleRejectTierSuffix = useCallback((calibreName: string) => {
    setCalibres((prev) =>
      prev.map((c) =>
        c.calibreName === calibreName
          ? {
              ...c,
              status: 'pending',
              source: undefined,
              id: undefined,
              data: undefined,
              normalizedName: undefined,
              tierId: undefined,
              tierName: undefined,
              tierCleared: undefined,
              tierSource: undefined,
              tierCandidates: undefined,
            }
          : c,
      ),
    );
  }, []);

  // The matched calibre's revision line, read off the loaded catalogue (no reads).
  const revisionLineFor = (id: string | undefined): RevisionLineMember[] | undefined => {
    const lineId = id ? localDb.find((c) => c.id === id)?.calibreLineageId : undefined;
    if (!lineId) return undefined;
    return localDb
      .filter((c) => c.calibreLineageId === lineId && c.deprecated !== true)
      .map((c) => ({
        id: c.id,
        name: c.name,
        calibreLineageId: c.calibreLineageId,
        revisionLabel: c.revisionLabel,
        activeFromYear: c.activeFromYear,
        activeUntilYear: c.activeUntilYear,
        isCurrent: c.isCurrent,
        isLineagePlaceholder: c.isLineagePlaceholder,
        data: c.data,
      }));
  };

  // Proceed with calibre map
  const handleProceed = () => {
    const calibreMap = new Map<string, CalibreMapEntry>();
    calibres.forEach((cal) => {
      if (cal.status === 'found' && cal.data) {
        calibreMap.set(cal.calibreName, {
          data: cal.data,
          id: cal.id,
          normalizedName: cal.normalizedName,
          tierId: cal.tierId,
          tierName: cal.tierName,
          tierCleared: cal.tierCleared,
          revisionLine: revisionLineFor(cal.id),
        });
      }
    });
    onProceed(calibreMap);
  };

  // Extraction rules management
  const addExtractionRule = () => {
    onExtractionRulesChange([...extractionRules, { id: `er-${++ruleId}`, sourceField: 'description', pattern: '', targetField: 'calibre' }]);
  };

  const updateExtractionRule = (id: string, field: string, value: string) => {
    onExtractionRulesChange(extractionRules.map((r) => r.id === id ? { ...r, [field]: value } : r));
  };

  const removeExtractionRule = (id: string) => {
    onExtractionRulesChange(extractionRules.filter((r) => r.id !== id));
  };

  const foundCount = calibres.filter((c) => c.status === 'found').length;
  const localMatchCount = calibres.filter((c) => c.status === 'found' && c.source === 'local').length;
  const pendingCount = calibres.filter((c) => c.status === 'pending').length;
  // Unresolved rows the extractor also scraped a calibre page for — i.e. how
  // many "From Extractor" actions are on offer. Surfaced in the summary line so
  // the affordance isn't only discoverable by scanning the table.
  const extractorAvailableCount = calibres.filter(
    (c) =>
      c.status !== 'found' &&
      c.status !== 'checking' &&
      extractorIndex.has(extractorCalibreKey(c.calibreName)),
  ).length;

  // Curator documentation for the brand this import belongs to. Fetched by id
  // because this step is handed a brandId, never a brand document.
  const { docs: brandDocs } = useDocsLinks('brand', brandId);

  // Refresh local DB whenever the user closes the Custom Calibres modal —
  // they may have added new entries that should now resolve locally.
  const handleCustomCalibresClose = () => {
    setShowCustomCalibres(false);
    void refreshLocalDb();
  };


  return (
    <div className="space-y-4">
      {/* Header with back button and DB buttons */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm text-gray-600">
            Found <span className="font-medium">{calibres.length}</span> unique calibres across{' '}
            <span className="font-medium">{entries.length}</span> entries.
          </p>
          {localDbLoaded && (
            <p className="text-xs text-gray-400 mt-1">
              {localMatchCount} matched{pendingCount > 0 ? ` • ${pendingCount} unmatched` : ''}
              {extractorAvailableCount > 0 && (
                <span className="text-amber-600"> • {extractorAvailableCount} have extractor data</span>
              )}
            </p>
          )}
          {/* One chip for the step, not one per row: this whole import is scoped
              to a single brand, so a per-row link would repeat the same
              destination on every unmatched calibre. */}
          {brandDocs.length > 0 && (
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500">
                Before creating calibres for {brandName}:
              </span>
              <DocsLinkChip
                links={brandDocs}
                entityLabel={brandName}
                hint="Naming and classification conventions for this brand."
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowCustomCalibres(true)}>
            Calibre Database
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setShowSlugMappings(true)}>
            Slug Mappings
          </Button>
          <Button variant="secondary" size="sm" onClick={onBack}>
            Back
          </Button>
        </div>
      </div>

      {/* Extraction rules */}
      {extractionRules.length > 0 && (
        <div className="border rounded-lg p-3 space-y-2 bg-gray-50 overflow-x-auto">
          <p className="text-xs font-medium text-gray-500 uppercase">Calibre Extraction Rules</p>
          {extractionRules.map((rule) => (
            <div key={rule.id} className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500">Extract from</span>
              <select
                value={rule.sourceField}
                onChange={(e) => updateExtractionRule(rule.id, 'sourceField', e.target.value)}
                className="text-sm border rounded px-2 py-1"
              >
                {EXTRACTION_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <span className="text-xs text-gray-500">matching</span>
              <input
                type="text"
                value={rule.pattern}
                onChange={(e) => updateExtractionRule(rule.id, 'pattern', e.target.value)}
                placeholder="regex pattern (group 1 captured)..."
                className="text-sm border rounded px-2 py-1 flex-1 font-mono"
              />
              <span className="text-xs text-gray-500">→</span>
              <input
                type="text"
                value={rule.replaceWith || ''}
                onChange={(e) => updateExtractionRule(rule.id, 'replaceWith', e.target.value)}
                placeholder="use as-is (or type replacement)..."
                className="text-sm border rounded px-2 py-1 flex-1"
              />
              <span className="text-xs text-gray-500">into</span>
              <select
                value={rule.targetField || 'calibre'}
                onChange={(e) => updateExtractionRule(rule.id, 'targetField', e.target.value)}
                className="text-sm border rounded px-2 py-1"
              >
                <option value="calibre">Calibre</option>
                <option value="movement">Base Movement</option>
                <option value="module">Module</option>
              </select>
              {testResults?.has(rule.id) && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${testResults.get(rule.id)! > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                  {testResults.get(rule.id)}
                </span>
              )}
              <button onClick={() => removeExtractionRule(rule.id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={addExtractionRule}>
          + Extraction Rule
        </Button>
        {extractionRules.length > 0 && (
          <Button variant="secondary" size="sm" onClick={() => {
            const counts = new Map<string, number>();
            for (const rule of extractionRules) {
              let matchCount = 0;
              for (const e of entries) {
                const fieldVal = String((e as Record<string, unknown>)[rule.sourceField] || '');
                if (!fieldVal) continue;
                try {
                  const regex = new RegExp(rule.pattern, 'i');
                  if (regex.test(fieldVal)) matchCount++;
                } catch { /* invalid regex */ }
              }
              counts.set(rule.id, matchCount);
            }
            setTestResults(counts);
          }}>
            Test Rules
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={() => { setShowJson(true); setJsonLimit(10); }}>
          View JSON
        </Button>
      </div>

      {/* Calibre table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Calibre</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-16">Used</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source / Info</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {resolving && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-500">
                    Resolving calibres…
                    <span className="block text-xs text-gray-400">
                      Matching against the calibre database and loading tier
                      vocabularies.
                    </span>
                  </td>
                </tr>
              )}
              {!resolving && calibres.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-400">
                    No calibre values found in this import.
                  </td>
                </tr>
              )}
              {calibres.map((cal, i) => {
                const revisionNotice = revisionNoticeFor(cal);
                const aiEligible = cal.status === 'pending' || cal.status === 'not_found' || cal.status === 'error';
                const hasExtractorData = extractorIndex.has(extractorCalibreKey(cal.calibreName));
                // Tiers this brand may assign on the calibre the row resolved to.
                // Empty for an unresolved row, and for a calibre with no tiers —
                // both render no selector at all rather than an empty dropdown on
                // every ETA row in the import.
                const rowTiers =
                  cal.status === 'found' ? tiersForBrand(cal.data?.tiers, brandId) : [];
                return (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs text-gray-900">
                      {cal.calibreName}
                      {cal.normalizedName && cal.normalizedName !== cal.calibreName && (
                        <span className="text-blue-600 ml-1">→ {cal.normalizedName}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{cal.count}×</td>
                    <td className="px-3 py-2">
                      {cal.status === 'pending' && <Badge variant="default">Pending</Badge>}
                      {cal.status === 'checking' && <Badge variant="info">Checking...</Badge>}
                      {cal.status === 'found' && (cal.source === 'local' || cal.source === 'local-tier-suffix') && <Badge variant="success">Local</Badge>}
                      {cal.status === 'found' && cal.source === 'ai' && <Badge variant="success">AI</Badge>}
                      {cal.status === 'found' && cal.source !== 'local' && cal.source !== 'local-tier-suffix' && cal.source !== 'ai' && <Badge variant="success">Found</Badge>}
                      {cal.status === 'not_found' && <Badge variant="warning">Not Found</Badge>}
                      {cal.status === 'error' && <Badge variant="danger">Error</Badge>}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-400">
                      {cal.source === 'local-tier-suffix' ? (
                        <span className="text-indigo-700">
                          {cal.normalizedName} · tier {cal.tierName}
                        </span>
                      ) : (
                        cal.source && <span className="capitalize">{cal.source}</span>
                      )}
                      {cal.data?.jewels && <span className="ml-2">{cal.data.jewels}j</span>}
                      {cal.data?.powerReserveHours && <span className="ml-2">{cal.data.powerReserveHours}h PR</span>}
                      {cal.data?.frequencyVph && <span className="ml-2">{cal.data.frequencyVph} vph</span>}
                      {revisionNotice && (
                        <span className="block text-amber-700" title="This calibre is part of a revision line and is not its current revision">
                          ⚠ {revisionNotice}
                        </span>
                      )}
                      {cal.status === 'not_found' && <span className="text-yellow-600">{cal.error}</span>}
                      {cal.status === 'error' && <span className="text-red-500">{cal.error}</span>}
                    </td>
                    <td className="px-3 py-2">
                      {rowTiers.length > 0 && (
                        <div className="mb-1 flex items-center gap-1.5">
                          <label className="sr-only" htmlFor={`tier-${i}`}>
                            Tier for {cal.normalizedName || cal.calibreName}
                          </label>
                          <select
                            id={`tier-${i}`}
                            value={cal.tierId ?? ''}
                            disabled={resolving || bulkCreating}
                            onChange={(e) => handlePickTier(cal.calibreName, e.target.value)}
                            title={`Tier for every reference using “${cal.calibreName}” (${cal.count}×)`}
                            className="rounded border-gray-300 text-xs py-0.5 disabled:opacity-50"
                          >
                            <option value="">— No tier —</option>
                            {rowTiers.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                          {cal.tierId && cal.tierSource === 'auto' && (
                            <span
                              className="text-[10px] text-gray-400"
                              title="Detected from the calibre string — change it here if that reading is wrong"
                            >
                              auto
                            </span>
                          )}
                        </div>
                      )}
                      {cal.source === 'local-tier-suffix' && (
                        <button
                          type="button"
                          onClick={() => handleRejectTierSuffix(cal.calibreName)}
                          disabled={resolving || bulkCreating}
                          title={`This string is not “${cal.normalizedName}” with a suffix at all — return the row to pending. To keep the calibre but drop the tier, use the selector instead.`}
                          className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                        >
                          Wrong calibre
                        </button>
                      )}
                      {(cal.tierCandidates?.length ?? 0) > 1 && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-xs text-gray-500">Reads as:</span>
                          {cal.tierCandidates?.map((c) => (
                            <button
                              key={`${c.calibreId}-${c.tierId}`}
                              type="button"
                              onClick={() => handlePickTierCandidate(cal.calibreName, c)}
                              className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 hover:bg-indigo-200"
                            >
                              {c.calibreName} · tier {c.tierName}
                            </button>
                          ))}
                        </div>
                      )}
                      {aiEligible && (
                        <div className="inline-flex items-center gap-1.5 flex-wrap">
                          <CalibreActionToolbar
                            calibreName={cal.calibreName}
                            brandName={brandName}
                            brandId={brandId}
                            showPushToDb={false}
                            showCreateNew={true}
                            compact={true}
                            disabled={resolving || bulkCreating}
                            onCalibreCreated={(docId) => handleToolbarSuccess(cal.calibreName, docId)}
                            onAliasAdded={(calibreId) => handleToolbarSuccess(cal.calibreName, calibreId)}
                          />
                          {hasExtractorData && (
                            <button
                              type="button"
                              onClick={() => handleOpenExtractorSeed(cal.calibreName)}
                              disabled={resolving || bulkCreating}
                              title="Create from the specs the extractor scraped for this calibre — opens the editor pre-filled for review"
                              className={`inline-flex items-center gap-1 rounded-full font-medium transition-colors px-2 py-0.5 text-xs ${
                                resolving || bulkCreating
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer'
                              }`}
                            >
                              <span>📄</span>
                              From Extractor
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {calibres.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-400">No calibres found — try adding extraction rules</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs bg-blue-50 text-blue-700 p-3 rounded">
        Matches resolve instantly from the custom calibre DB, including calibres reached through a
        brand-scoped alias rather than their primary brand. Edits made inside this wizard re-match
        automatically; use "Rescan Matches" after changing a calibre in another tab. Add missing
        calibres via the Calibre Database / Slug Mappings buttons.
      </p>

      {/* Action buttons */}
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            onClick={handleRescan}
            disabled={resolving || rescanning || bulkCreating}
            loading={rescanning}
            title="Re-read the calibre database and re-run the match. Use after editing a calibre in another tab — changes made in this wizard already re-match on their own."
          >
            {rescanning ? 'Rescanning…' : 'Rescan Matches'}
          </Button>
          {unmatchedCalibres.length > 0 && (
            <Button
              variant="secondary"
              onClick={handleCreateAllUnmatched}
              disabled={resolving || bulkCreating}
              loading={bulkCreating}
            >
              Create All Unmatched ({unmatchedCalibres.length})
            </Button>
          )}
          {extractorUnmatched.length > 0 && (
            <Button
              variant="secondary"
              onClick={handleCreateAllFromExtractor}
              disabled={resolving || bulkCreating}
              loading={bulkCreating}
              title="Create every row the extractor scraped a calibre page for, using those specs. Skips the per-row review form; rows with no scraped page are left alone."
            >
              📄 Create All From Extractor ({extractorUnmatched.length})
            </Button>
          )}
        </div>
        <Button
          onClick={handleProceed}
          disabled={resolving}
          title={
            resolving
              ? 'Waiting for the calibre database and tier vocabularies — proceeding now would import every reference with no calibre link.'
              : undefined
          }
        >
          {resolving
            ? 'Resolving…'
            : pendingCount === 0
              ? `Proceed → (${foundCount} enriched)`
              : 'Skip & Proceed →'}
        </Button>
      </div>

      {/* Modals */}
      <CalibreSlugMappingsModal isOpen={showSlugMappings} onClose={() => setShowSlugMappings(false)} />
      <CustomCalibreModal isOpen={showCustomCalibres} onClose={handleCustomCalibresClose} currentBrandName={brandName} defaultBrandId={brandId} />

      {/* "From Extractor" create form. A separate instance from the database
          browser above because it opens straight into create mode with a seed;
          keyed on the row so switching rows remounts with fresh seed values
          rather than reusing the modal's open-time snapshot. */}
      {extractorSeed && extractorSeedRow && (
        <CustomCalibreModal
          key={extractorSeedRow}
          isOpen
          onClose={closeExtractorSeed}
          currentBrandName={brandName}
          defaultBrandId={brandId}
          defaultManufacturerId={extractorSeedManufacturerId}
          initialMode="create"
          initialCreateValues={extractorSeed}
          requestNote={`Pre-filled from the extractor's scraped calibre page${extractorSeed.sourceUrl ? ` (${extractorSeed.sourceUrl})` : ''}. Review every field before saving — the scrape is unparsed source text and units are best-effort.`}
          onSaved={(savedId) => handleToolbarSuccess(extractorSeedRow, savedId)}
        />
      )}

      {/* JSON Viewer */}
      {showJson && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowJson(false)}>
          <div className="bg-white rounded-lg shadow-xl w-[90vw] max-w-4xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-medium">Import Data JSON ({entries.length} entries)</h3>
              <button onClick={() => setShowJson(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs font-mono bg-gray-900 text-green-400">
              {JSON.stringify(entries.slice(0, jsonLimit), null, 2)}
            </pre>
            <div className="px-4 py-2 border-t flex items-center justify-between">
              <span className="text-xs text-gray-400">
                Showing {Math.min(jsonLimit, entries.length)} of {entries.length} entries
              </span>
              {jsonLimit < entries.length && (
                <button
                  onClick={() => setJsonLimit((prev) => Math.min(prev + 20, entries.length))}
                  className="text-xs text-brand-600 hover:text-brand-800 font-medium"
                >
                  Load more ({Math.min(20, entries.length - jsonLimit)} more)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
